/**
 * Buyin Error Popup for Poker Reborn
 *
 * Displays an error when the player cannot take a seat.
 * Shows the server's error reason (e.g., "Not enough chips to buyin").
 *
 * @see tasks/active/T07_BUYIN_ERROR_HANDLING_PLAN.md - Implementation details
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, PRIMARY_BUTTON_STYLE } from '@/components/common/Button';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Overlay */
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },

  /** Popup */
  popup: {
    width: 380,
    height: 250,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
  },

  /** Title */
  title: {
    fill: 0xff6b6b,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Error message */
  message: {
    fill: 0xcccccc,
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: 22,
    wordWrap: true,
    wordWrapWidth: 340,
    align: 'center' as const,
  },
};

// =============================================================================
// BUYIN ERROR POPUP
// =============================================================================

export class BuyinErrorPopup extends Container {
  private overlay: Graphics;
  private popup: Container;
  private background: Graphics;
  private titleText: Text;
  private messageText: Text;
  private okButton: Button;

  private screenWidth: number;
  private screenHeight: number;

  /** Callback when player dismisses */
  onDismiss: (() => void) | null = null;

  constructor(screenWidth: number, screenHeight: number, errorMessage: string) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create overlay
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointerdown', () => {
      this.handleDismiss();
    });
    this.addChild(this.overlay);

    // Create popup container
    this.popup = new Container();
    this.popup.x = screenWidth / 2;
    this.popup.y = screenHeight / 2;
    this.addChild(this.popup);

    // Create background
    this.background = new Graphics();
    this.popup.addChild(this.background);
    this.drawBackground();

    // Stop popup clicks from closing
    this.background.eventMode = 'static';
    this.background.on('pointerdown', (e) => {
      e.stopPropagation();
    });

    // Create title
    this.titleText = new Text({
      text: 'Cannot Take Seat',
      style: STYLES.title,
    });
    this.titleText.anchor.set(0.5);
    this.titleText.y = -75;
    this.popup.addChild(this.titleText);

    // Create error message
    this.messageText = new Text({
      text: errorMessage,
      style: STYLES.message,
    });
    this.messageText.anchor.set(0.5);
    this.messageText.y = -15;
    this.popup.addChild(this.messageText);

    // Create OK button
    const buttonWidth = 140;
    const buttonHeight = 44;
    const buttonY = 55;

    this.okButton = new Button({
      label: 'OK',
      width: buttonWidth,
      height: buttonHeight,
      style: PRIMARY_BUTTON_STYLE,
    });
    this.okButton.onClick = () => {
      this.handleDismiss();
    };
    this.okButton.x = -buttonWidth / 2;
    this.okButton.y = buttonY;
    this.popup.addChild(this.okButton);
  }

  /**
   * Resize popup
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Resize overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Reposition popup
    this.popup.x = width / 2;
    this.popup.y = height / 2;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawBackground(): void {
    const { width, height, fill, stroke, cornerRadius } = STYLES.popup;

    this.background.clear();
    this.background.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
    this.background.fill(fill);
    this.background.setStrokeStyle({ width: 2, color: stroke });
    this.background.stroke();
  }

  private handleDismiss(): void {
    if (this.onDismiss) {
      this.onDismiss();
    }
    this.destroy();
  }
}
